/** Same-origin triage pilot only. Host hints never establish student authority.
 * Cross-origin staging/redemption requires the separately gated U3/U4 contract.
 */
type LogoutUpdate = { state: string; at: number; nonce: string };
type PilotState = { attempted: boolean; fenced: boolean; consumerFenced: boolean; active: boolean; replacementRequired: boolean; logout: LogoutUpdate | null };
const states = new Map<string, PilotState>();
const owners = new Map<string, { count: number; dispose: () => void }>();
const logoutKey = "phoebe_triage_host_logout_state";
const activationKey = "phoebe_triage_widget_activation";
const rank = (state: string) => ["pending", "cancelled", "complete"].indexOf(state);

/** One owner per backend/document, shared by IIFE and React library mounts. */
export function acquireSilentWidgetAuth(backendUrl: string): () => void {
  let key: string;
  try {
    const backend = new URL(backendUrl);
    if (backend.username || backend.password || backend.pathname !== "/" || backend.search || backend.hash ||
        !(backend.protocol === "https:" || backend.origin === "http://localhost:8000") || location.origin === "null") return () => {};
    key = backend.origin;
  } catch { return () => {}; } // Invalid pilot configuration must not unmount the guest widget.
  let owner = owners.get(key);
  if (!owner) {
    const state = states.get(key) ?? { attempted: false, fenced: false, consumerFenced: false, active: false, replacementRequired: false, logout: null };
    states.set(key, state);
    owner = { count: 0, dispose: install(key, state) };
    owners.set(key, owner);
  } else if (states.get(key)?.consumerFenced) {
    // A newly mounted consumer missed the owner's earlier disconnect notification.
    window.dispatchEvent(new Event("phoebe:widget-auth-fence"));
  }
  owner.count++;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--owner.count === 0) {
      owner.dispose();
      owners.delete(key);
    }
  };
}

function install(backend: string, state: PilotState): () => void {
  const callback = location.origin + (location.hostname === "localhost"
    ? "/dev/phoebe/silent-callback" : "/phoebe/silent-callback");
  const abort = new AbortController();
  const channels: BroadcastChannel[] = [];
  const source = crypto.randomUUID();
  let generation = 0;
  let hostAuthenticated = false;
  let frame: HTMLIFrameElement | null = null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let renewal: ReturnType<typeof setTimeout> | undefined;
  let resultListener: ((event: MessageEvent) => void) | null = null;
  let lastActivation: string | null = null;
  const emit = (name: string) => {
    state.consumerFenced = name === "fence";
    window.dispatchEvent(new Event(`phoebe:widget-auth-${name}`));
  };
  const live = (current: number) => !abort.signal.aborted && current === generation;
  const stop = () => {
    generation++;
    clearTimeout(timer);
    clearTimeout(renewal);
    timer = renewal = undefined;
    if (resultListener) window.removeEventListener("message", resultListener);
    resultListener = null;
    frame?.remove();
    frame = null;
  };
  const request = (path: string, body?: object) => fetch(new URL(`/v1/${path}`, backend), {
    credentials: "include", cache: "no-store", signal: abort.signal,
    ...(body ? { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) } : {}),
  });
  const schedule = (seconds: unknown) => {
    if (typeof seconds !== "number" || !Number.isSafeInteger(seconds) || seconds < 1 || seconds > 86400) return;
    clearTimeout(renewal);
    renewal = setTimeout(() => { state.attempted = false; void start(true); }, seconds * 1000);
  };
  const recover = async (current: number, signedOut = false) => {
    if (!live(current) || (!state.active && !state.consumerFenced) || (state.fenced && !signedOut)) return;
    // The attempt deadline bounds authentication, not durable revocation. It must
    // not invalidate this recovery generation while logout is still in flight.
    clearTimeout(timer);
    timer = undefined;
    if (!state.consumerFenced) emit("fence");
    // Keep the fence if durable revocation is uncertain. Never reconnect the old credential.
    state.fenced = true;
    try {
      const response = await request("silent-widget-auth/logout", {});
      if (!live(current) || !response.ok) return;
      state.active = false;
      state.replacementRequired = false;
      state.fenced = false; // Durable revocation permits guest mode, not another silent attempt.
      if (signedOut) {
        // Retire the missed pending hint before reconnecting. Late transport copies
        // must not refence this verified transition, including after a remount.
        const update = { state: "complete", at: Math.max(Date.now(), (state.logout?.at ?? 0) + 1), nonce: crypto.randomUUID() };
        state.logout = update;
        try { localStorage.setItem(logoutKey, JSON.stringify(update)); } catch { /* Other transport remains. */ }
        for (const channel of channels) if (channel.name === logoutKey.replace("_state", "")) channel.postMessage(update);
      }
      emit("guest");
    } catch { /* Retain fence. */ }
  };
  const adopt = async (current: number, publish = false) => {
    const response = await request("silent-widget-auth/status");
    if (!response.ok || !live(current)) return false;
    const session = await response.json();
    if (!live(current) || state.fenced || !hostAuthenticated || session.authenticated !== true) return false;
    // Verified adoption wins over any local attempt, including already queued
    // callbacks/status reads. Retire its frame/listener/generation before emit.
    stop();
    state.active = true;
    state.replacementRequired = false;
    emit("activated");
    schedule(session.renew_after_seconds);
    if (publish) {
      const update = { nonce: crypto.randomUUID(), source };
      try { localStorage.setItem(activationKey, JSON.stringify(update)); } catch { /* Other transport remains. */ }
      for (const channel of channels) if (channel.name === activationKey) channel.postMessage(update);
    }
    return true;
  };
  const start = async (renew = false, adoptionOnly = false) => {
    if (abort.signal.aborted || state.fenced || !hostAuthenticated || (state.attempted && !adoptionOnly)) return;
    renew ||= state.replacementRequired;
    const previouslyAttempted = state.attempted;
    state.attempted = true;
    const current = ++generation;
    const deadline = performance.now() + 15000;
    // Status, start and frame share one deadline; no phase restarts the clock.
    timer = setTimeout(() => { stop(); void recover(generation); }, 15000);
    try {
      const response = await request("silent-widget-auth/status");
      if (!live(current)) return;
      if (!response.ok) { stop(); await recover(generation); return; }
      const session = await response.json();
      if (!live(current) || state.fenced) return;
      if (session.authenticated_cookie === true) state.active = true;
      if (session.authenticated === true && !renew) {
        clearTimeout(timer);
        state.active = true;
        emit("activated"); // Existing cookie still needs a credentialed Socket.IO reconnect.
        schedule(session.renew_after_seconds);
        return;
      }
      if ((adoptionOnly && previouslyAttempted) || typeof session.authenticated !== "boolean") {
        stop();
        await recover(generation);
        return;
      }
      const started = await request("silent-widget-auth/start", { redirect_uri: callback });
      if (!live(current)) return;
      if (!started.ok) { stop(); await recover(generation); return; }
      const body = await started.json();
      if (!live(current)) return;
      const destination = new URL(body.authorization_url);
      if (destination.origin !== "https://login-staging.phoenix.edu" ||
          destination.pathname !== "/am/oauth2/authorize" || destination.username || destination.password || destination.hash) {
        stop(); await recover(generation); return;
      }
      const activeFrame = document.createElement("iframe");
      frame = activeFrame;
      activeFrame.hidden = true;
      activeFrame.tabIndex = -1;
      activeFrame.setAttribute("aria-hidden", "true");
      activeFrame.title = "Super Phoebe background sign-in";
      activeFrame.src = destination.href;
      resultListener = (event) => {
        if (!live(current) || event.origin !== location.origin || event.source !== activeFrame.contentWindow ||
            event.data?.type !== "phoebe:silent-auth-result" || typeof event.data.authenticated !== "boolean") return;
        const succeeded = event.data.authenticated === true;
        // Completion may already have installed a cookie. This is recovery
        // bookkeeping only; activation still requires verified backend status.
        if (succeeded) state.active = true;
        stop();
        const resultGeneration = generation;
        timer = setTimeout(() => { stop(); void recover(generation); }, Math.max(0, deadline - performance.now()));
        // A token-free notification is only a hint; verify the actual cookie before adoption.
        void (async () => {
          try {
            if (succeeded && await adopt(resultGeneration, true)) return;
          } catch { /* Recover without exporting provider errors. */ }
          await recover(resultGeneration);
          if (live(resultGeneration)) clearTimeout(timer);
        })();
      };
      window.addEventListener("message", resultListener);
      document.body.appendChild(activeFrame);
    } catch {
      if (live(current)) { stop(); await recover(generation); }
    }
  };
  const acceptLogout = (update: LogoutUpdate, initializing = false) => {
    if (!update || rank(update.state) < 0 || !Number.isSafeInteger(update.at) || update.at < 0 ||
        typeof update.nonce !== "string" || !update.nonce) return;
    const last = state.logout;
    if (last && (update.at < last.at || (update.at === last.at &&
        (rank(update.state) < rank(last.state) || (rank(update.state) === rank(last.state) && update.nonce <= last.nonce))))) return;
    state.logout = update;
    if (initializing) {
      if (last && last.at === update.at && last.state === update.state && last.nonce === update.nonce) return;
      if (update.state === "cancelled" && state.fenced) state.replacementRequired = true;
      // Stored terminal hints are ordering watermarks, not a fresh logout.
      // Replaying complete here would rotate the guest scope on every reload.
      state.fenced = update.state === "pending";
      if (update.state !== "pending") state.attempted = state.active = false;
      return;
    }
    if (update.state === "pending") {
      stop();
      state.fenced = true;
      hostAuthenticated = false;
      emit("fence");
    } else if (update.state === "complete") {
      stop();
      state.fenced = false;
      state.active = false;
      state.replacementRequired = false;
      state.attempted = false;
      hostAuthenticated = false;
      emit("guest");
    } else if (state.fenced) {
      stop();
      state.fenced = false;
      state.attempted = false;
      hostAuthenticated = true;
      state.replacementRequired = true;
      void start(true); // Backend verifies cancellation and issues a fresh credential.
    }
  };
  const acceptActivation = (update: { source?: string; nonce?: string }) => {
    if (!update || update.source === source || typeof update.nonce !== "string" || !update.nonce ||
        lastActivation === update.nonce || state.fenced || state.replacementRequired || !hostAuthenticated) return;
    lastActivation = update.nonce;
    void adopt(generation).catch(() => {});
  };
  const on = (name: string, listener: EventListener) => window.addEventListener(name, listener, { signal: abort.signal });
  on("phoebe:host-auth", ((event: CustomEvent) => {
    hostAuthenticated = event.detail?.authenticated === true;
    if (hostAuthenticated) { if (!state.fenced) void start(); return; }
    stop();
    if (state.fenced) return;
    const active = state.active;
    state.attempted = state.active = false;
    if (active) emit("guest");
  }) as EventListener);
  on("phoebe:host-auth-logout-start", ((event: CustomEvent) => {
    if (event.detail) { acceptLogout(event.detail); return; }
    stop(); state.fenced = true; hostAuthenticated = false; emit("fence");
  }) as EventListener);
  on("phoebe:host-auth-logout-cancelled", () => {
    // The host helper stores/publishes before this synchronous hook. Consume the
    // same watermark now so delayed pending/terminal transport copies are inert.
    try {
      const update = JSON.parse(localStorage.getItem(logoutKey) ?? "null");
      if (update?.state === "cancelled") { acceptLogout(update); return; }
    } catch { /* Storage is optional; the fresh grant still requires backend checks. */ }
    if (!state.fenced) return;
    state.replacementRequired = true;
    stop(); state.fenced = false; state.attempted = false; hostAuthenticated = true; void start(true);
  });
  on("storage", ((event: StorageEvent) => {
    try {
      if (event.key === logoutKey && event.newValue) acceptLogout(JSON.parse(event.newValue));
      if (event.key === activationKey && event.newValue) acceptActivation(JSON.parse(event.newValue));
    } catch { /* Ignore malformed local hints. */ }
  }) as EventListener);
  for (const name of [logoutKey.replace("_state", ""), activationKey]) {
    try {
      const channel = new BroadcastChannel(name);
      channels.push(channel);
      channel.onmessage = (event) => name === activationKey ? acceptActivation(event.data) : acceptLogout(event.data);
    } catch { /* localStorage is the other transport. */ }
  }
  try { acceptLogout(JSON.parse(localStorage.getItem(logoutKey) ?? "null"), true); } catch { /* Backend remains authority. */ }
  if (state.fenced || state.consumerFenced) emit("fence");
  // The host helper can finish before React mounts. Bootstrap from server status,
  // never from a cached Signed in label. Pending status cannot lift a logout fence.
  const initial = generation;
  void (async () => {
    try {
      const response = await request("triage-auth/status");
      if (!live(initial) || !response.ok) return;
      const host = await response.json();
      if (!live(initial)) return;
      hostAuthenticated = host.authenticated === true;
      if (host.logout_pending === true) { state.fenced = true; emit("fence"); return; }
      if (!hostAuthenticated) {
        state.attempted = false;
        if (host.authenticated === false && host.logout_pending === false && state.consumerFenced) {
          stop();
          await recover(generation, true);
        }
        return;
      }
      if (!state.fenced) await start(false, true);
    } catch { /* No authority, no attempt. */ }
  })();
  return () => { stop(); abort.abort(); channels.forEach((channel) => channel.close()); };
}
